/**
 * `run-report-logger`, opt-in JSON logger for finished runs in time, used to
 * calibrate the journey against real player data.
 *
 * Pure TS, zero dependency on React / Studio / Pixi / DOM. Consumers
 * (typically `RhythmState` / `SpeedupState` via the `useRhythmRun` hook)
 * call `logRunReport(...)` after each run; the logger checks whether
 * logging is enabled and, if so, emits a single JSON line per run.
 *
 * Activation
 * ----------
 * Three independent gates, evaluated in this order:
 *
 *  1. **Direct call argument**, `logRunReport({ enabled: true, ... })`
 *     short-circuits everything else. Useful for tests.
 *  2. **`localStorage` flag**, `localStorage.getItem('virtuosa:journey:run-log')`
 *     equals `'1'` (or `'true'`). Toggleable by the user via the
 *     DebugOverlay (see `DebugOverlay.tsx` integration point) or
 *     directly from devtools:
 *
 *         localStorage.setItem('virtuosa:journey:run-log', '1');
 *
 *  3. **Process env var** (server / test), `process.env.VIRTUOSA_RUN_LOG`.
 *     The browser bundle never sees this; it's only for offline
 *     calibration runs (`scripts/...`).
 *
 * If none of the gates is on, the logger is a no-op, no JSON.stringify,
 * no console call, nothing on the hot path.
 *
 * Output format
 * -------------
 * One JSON line per run, written to `console.info`. Lines are tagged
 * with the `[journey-run-log]` prefix so the user can grep / pipe them
 * out of devtools easily:
 *
 *     [journey-run-log] {"v":1,"phraseId":"...", ... }
 *
 * The `v` field is a small schema version: bump it when the shape
 * changes so post-processing scripts can branch on it.
 */

import type { RunReading } from '@/components/learn-piece/rhythm';
import type { HandsAsked, PhraseId } from '../types';

// ── Public types ─────────────────────────────────────────────────

/**
 * The exact JSON line emitted by the logger. Kept as a `type` (not an
 * interface) because consumers occasionally pass it through helpers
 * that flatten unions, keeping the structure index-signature-friendly
 * helps the typechecker.
 *
 * Field naming is intentionally short: this gets serialized to a JSON
 * line per run, sometimes thousands of lines per session.
 */
export type RunLogEntry = {
  /** Schema version, bump on shape changes. */
  readonly v: 1;
  /** Wall-clock ISO timestamp at which the entry was recorded. */
  readonly t: string;
  /** Identifier of the phrase the run targeted. */
  readonly phraseId: PhraseId | string;
  /** FSM state when the run was completed (`S4`, `S5`, `phase2`). */
  readonly state: 'S4' | 'S5' | 'phase2' | string;
  /** Which hands the run asked for. Read off the unit; see `handsAsked`. */
  readonly mode: HandsAsked;
  /** BPM in effect (the song's nominal tempo, not multiplied by rate). */
  readonly bpm: number;
  /** Playback rate the player chose for this run. */
  readonly playbackRate: number;
  /** How the run ended, as the view read it. */
  readonly outcome: 'validated' | 'failed' | 'cancelled';
  /**
   * Compact summary of the reading. Omitted when no reading was produced
   * (a cancelled run).
   */
  readonly report?: {
    readonly onsets: number;
    /** Onsets answered by one of their own keys, near their instant. */
    readonly honoured: number;
    /** Index of the onset the player first stopped on, or `-1`. */
    readonly stoppedAt: number;
    /** How many times they stopped. */
    readonly stops: number;
    /** The player's beat over the click's, when it could be read. */
    readonly pulseRatio?: number;
    /** Spread around their own beat, as a fraction of it. */
    readonly pulseSpread?: number;
    /**
     * Keys struck that the passage asks for nowhere near their instant.
     *
     * Logged because `STRAY_TOLERANCE_RATIO` is a **hypothesis**, exactly as
     * `TARGET_ATTACKS_PER_SECOND` is. The threshold it feeds withholds a rung
     * of the tempo ladder, and the only honest way to retune it is against
     * real passes — which is how its predecessor came to be removed.
     */
    readonly strays: number;
  };
};

export type LogRunReportInput = {
  readonly phraseId: PhraseId | string;
  readonly state: 'S4' | 'S5' | 'phase2' | string;
  readonly mode: HandsAsked;
  readonly bpm: number;
  readonly playbackRate: number;
  readonly outcome: 'validated' | 'failed' | 'cancelled';
  /** `null` when no reading was computed (cancelled run). */
  readonly report: RunReading | null;
  /**
   * Optional override that bypasses the activation gates. When `true`,
   * the entry is always emitted. Mainly intended for tests.
   */
  readonly enabled?: boolean;
  /**
   * Optional sink. Defaults to `console.info`. Tests inject a spy here
   * to capture the entry without monkey-patching the global console.
   */
  readonly sink?: (line: string) => void;
};

// ── Public API ───────────────────────────────────────────────────

const STORAGE_KEY = 'virtuosa:journey:run-log';
const LINE_PREFIX = '[journey-run-log]';
const ENV_VAR = 'VIRTUOSA_RUN_LOG';

/**
 * Emit a run-log entry if logging is enabled. No-op otherwise.
 *
 * Returns the produced entry (or `null` if disabled / suppressed) so
 * callers that want to surface it in-app (e.g. the DebugOverlay) can
 * read it without re-parsing the JSON line.
 */
export function logRunReport(input: LogRunReportInput): RunLogEntry | null {
  if (isLoggingEnabled(input.enabled) === false) {
    return null;
  }

  const entry = buildEntry(input);
  const sink = input.sink ?? defaultSink;
  sink(`${LINE_PREFIX} ${JSON.stringify(entry)}`);

  return entry;
}

/**
 * Read-only check for the current logging state. Used by the
 * DebugOverlay to render the "logging is ON" indicator without
 * triggering a log call.
 */
export function isRunLogEnabled(): boolean {
  return isLoggingEnabled(undefined);
}

/**
 * Toggle logging via the `localStorage` flag. Returns the new state.
 * No-op on environments where `localStorage` is unavailable.
 */
export function setRunLogEnabled(enabled: boolean): boolean {
  if (hasLocalStorage() === false) {
    return false;
  }

  const ls = (globalThis as { localStorage: Storage }).localStorage;

  if (enabled) {
    ls.setItem(STORAGE_KEY, '1');

    return true;
  }

  ls.removeItem(STORAGE_KEY);

  return false;
}

// ── Internal ─────────────────────────────────────────────────────

function buildEntry(input: LogRunReportInput): RunLogEntry {
  const base: RunLogEntry = {
    v: 1,
    t: new Date().toISOString(),
    phraseId: input.phraseId,
    state: input.state,
    mode: input.mode,
    bpm: input.bpm,
    playbackRate: input.playbackRate,
    outcome: input.outcome,
  };

  if (input.report === null) {
    return base;
  }

  return {
    ...base,
    report: summarizeReport(input.report),
  };
}

function summarizeReport(
  report: RunReading,
): NonNullable<RunLogEntry['report']> {
  return {
    onsets: report.honoured.length,
    honoured: report.honoured.filter(Boolean).length,
    stoppedAt: report.interruptions[0]?.groupIndex ?? -1,
    stops: report.interruptions.length,
    pulseRatio: report.pulse?.ratio,
    pulseSpread: report.pulse?.spread,
    strays: report.strays.length,
  };
}

function isLoggingEnabled(directOverride: boolean | undefined): boolean {
  if (directOverride === true) {
    return true;
  }

  if (directOverride === false) {
    return false;
  }

  if (hasLocalStorage()) {
    const flag = (globalThis as { localStorage: Storage }).localStorage.getItem(
      STORAGE_KEY,
    );

    if (flag === '1' || flag === 'true') {
      return true;
    }
  }

  if (typeof process !== 'undefined' && process.env !== undefined) {
    const envFlag = process.env[ENV_VAR];

    if (envFlag === '1' || envFlag === 'true') {
      return true;
    }
  }

  return false;
}

function hasLocalStorage(): boolean {
  if (typeof globalThis === 'undefined') {
    return false;
  }

  const ls = (globalThis as { localStorage?: Storage }).localStorage;

  return ls !== undefined && ls !== null;
}

/**
 * Default sink. Goes through `console.info` (not `console.log`) so the
 * lines are easy to filter by level in browser devtools.
 */
function defaultSink(line: string): void {
  // eslint-disable-next-line no-console
  console.info(line);
}
